import { useEffect, useState } from 'react';
import { Alert, Box, Button, Divider, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { ChoiceControl, DefinitionList, IdentityAvatar, ListRow, ResourceState, StatusBadge, designTokens } from '../../../design-system';
import type { BulkOperationDetail, BulkOperationReceipt } from '../model/bulkOperations';
import { bulkDateLabel, bulkStatusLabel, isBulkJobActive } from '../model/presentation';

export function BulkStatus({ value }: { value: string }) {
  const success = ['VALID', 'SUCCEEDED', 'VALIDATED', 'COMPLETED'].includes(value);
  const warning = ['PROCESSING', 'QUEUED', 'DRAFT'].includes(value);
  return <StatusBadge tone={success ? 'success' : warning ? 'warning' : ['FAILED', 'INVALID', 'COMPLETED_WITH_ERRORS'].includes(value) ? 'danger' : 'neutral'} label={bulkStatusLabel(value)} />;
}

export function BulkOperationResults({ job, onCorrect, busy = false, compact = false, receipt, onOpenStaffingChange }: {
  job: BulkOperationDetail;
  onCorrect?: () => void;
  busy?: boolean;
  compact?: boolean;
  receipt?: BulkOperationReceipt;
  onOpenStaffingChange?: (id: string) => void;
}) {
  const [failedOnly, setFailedOnly] = useState(false);
  const [visibleCount, setVisibleCount] = useState(25);
  useEffect(() => { setFailedOnly(false); setVisibleCount(25); }, [job.id]);
  const failedRows = job.rows.filter((row) => row.status === 'FAILED' || row.status === 'INVALID');
  const failedCount = Math.max(failedRows.length, job.failedRows);
  const pendingCount = Math.max(0, job.totalRows - job.succeededRows - failedCount);
  const staffing = job.operationType === 'STAFFING_CHANGE';
  const receiptByRow = new Map(receipt?.rows.map((row) => [row.rowNumber, row]));
  const rows = failedOnly ? failedRows : job.rows;
  const downloadErrors = () => {
    const escape = (value: string) => {
      const text = value.replaceAll('\t', ' ').replaceAll('\r', ' ').replaceAll('\n', ' ');
      return /^[=+@-]/.test(text.trimStart()) ? `'${text}` : text;
    };
    const text = ['행 번호\t처리 상태\t확인 사항', ...failedRows.map((row) => [row.rowNumber, bulkStatusLabel(row.status), escape(row.errorMessage ?? '오류 내용을 확인해 주세요.')].join('\t'))].join('\n');
    const url = URL.createObjectURL(new Blob(['\uFEFF', text], { type: 'text/tab-separated-values;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = '대량작업-실패행.tsv';
    link.click();
    URL.revokeObjectURL(url);
  };
  return <Stack spacing={2}>
    {compact ? <Stack spacing={0.5}>
      <Typography variant="body2">제출 {job.succeededRows.toLocaleString()}{staffing ? '명' : '건'} · 대기 {pendingCount.toLocaleString()}{staffing ? '명' : '건'} · 실패 {failedCount.toLocaleString()}{staffing ? '명' : '건'}</Typography>
      <Typography variant="body2" color="text.secondary">요청 제출 완료는 결재 완료와 다릅니다.</Typography>
    </Stack> : <><DefinitionList items={[
      ['상태', <BulkStatus value={job.status} />],
      ['요청 일시', bulkDateLabel(job.createdAt)],
      ['전체 행', `${job.totalRows.toLocaleString()}개`],
      ['유효 행', `${job.validRows.toLocaleString()}개`],
      ['성공', `${job.succeededRows.toLocaleString()}개`],
      ['실패', `${job.failedRows.toLocaleString()}개`],
    ]} />
    <Typography variant="body2" color="text.secondary">성공은 각 행의 요청 처리가 끝났다는 뜻이며, 결재가 필요한 요청은 별도로 승인됩니다.</Typography></>}
    {job.status === 'VALIDATED' && (job.failedRows > 0 || job.validRows !== job.totalRows) && <Alert severity="warning">오류 행이 포함된 작업은 적용할 수 없습니다. 입력을 수정해 다시 검증해 주세요.</Alert>}
    {failedRows.length > 0 && <Stack spacing={1}>
      {!compact && <Typography variant="body2" color="text.secondary">실패한 행만 원본과 대조해 수정해 주세요. 이미 성공한 행은 다시 등록하지 않습니다.</Typography>}
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
        {compact && <ChoiceControl kind="checkbox" checked={failedOnly} onCheckedChange={(checked) => { setFailedOnly(checked); setVisibleCount(25); }} label={`실패한 행만 보기 (${failedRows.length})`} />}
        <Button variant="outlined" size="small" disabled={busy} onClick={downloadErrors}>실패 목록 다운로드</Button>
        {onCorrect && <Button size="small" disabled={busy} onClick={onCorrect}>수정한 행 새로 입력</Button>}
      </Stack>
    </Stack>}
    {job.rows.length > 0 && <>
      {!compact && <ChoiceControl kind="checkbox" checked={failedOnly} onCheckedChange={(checked) => { setFailedOnly(checked); setVisibleCount(25); }} label={`실패한 행만 보기 (${failedRows.length})`} />}
      <ResourceState loading={false} empty={rows.length === 0} emptyTitle="실패한 행이 없습니다." embedded density="compact">
        {compact ? <TableContainer>
          <Table aria-label="대량 작업 행별 결과" sx={{ minWidth: { xs: 0, sm: 560 }, display: { xs: 'block', sm: 'table' } }}>
            <TableHead sx={{ display: { xs: 'none', sm: 'table-header-group' } }}><TableRow>
              <TableCell>{staffing ? '구성원' : '행'}</TableCell>
              <TableCell>{staffing && receipt ? '변경 내용' : '확인 사항'}</TableCell>
              <TableCell>처리 상태</TableCell>
            </TableRow></TableHead>
            <TableBody sx={{ display: { xs: 'block', sm: 'table-row-group' } }}>{rows.slice(0, visibleCount).map((row) => {
              const member = staffing ? receiptByRow.get(row.rowNumber) : undefined;
              const rowStatus = row.status === 'VALID' && isBulkJobActive(job.status) ? 'QUEUED' : row.status;
              const canOpenStaffingChange = staffing && onOpenStaffingChange && row.status === 'SUCCEEDED' && row.responseResourceType === 'JOB_CHANGE' && row.responseId;
              return <TableRow key={row.rowNumber} sx={(theme) => ({ verticalAlign: 'top',
                [theme.breakpoints.down('sm')]: {
                  display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 1, p: 2,
                  borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 },
                  '& > .MuiTableCell-root': { display: 'block', p: 0, borderBottom: 0 },
                },
              })}>
                <TableCell component="th" scope="row" sx={{ gridColumn: 1, gridRow: 1 }}>
                  {member ? <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 160 }}>
                    <IdentityAvatar src={member.avatarUrl} alt="" size={32}>{member.name.slice(0, 1)}</IdentityAvatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{member.name}</Typography>
                      {member.subtitle && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>{member.subtitle}</Typography>}
                    </Box>
                  </Stack> : `${row.rowNumber}행`}
                </TableCell>
                <TableCell sx={{ minWidth: { xs: 0, sm: 180 }, overflowWrap: 'anywhere', gridColumn: '1 / -1', gridRow: 2 }}>
                  <Stack spacing={0.5}>
                    {member?.changes.map((change, index) => <Typography key={`${change.label}-${index}`} variant="body2">
                      <Box component="span" sx={{ color: 'text.secondary' }}>{change.label} </Box>
                      <Box component="span" sx={{ color: change.before ? 'inherit' : designTokens.color.mutedText }}>{change.before || '없음'}</Box>{' → '}{change.after}
                    </Typography>)}
                    {row.errorMessage && <Typography variant="body2" color="error.main">{row.errorMessage}</Typography>}
                    {!member?.changes.length && !row.errorMessage && <Typography variant="body2" color="text.secondary">—</Typography>}
                  </Stack>
                </TableCell>
                <TableCell sx={{ gridColumn: 2, gridRow: 1 }}>
                  <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                    <BulkStatus value={rowStatus} />
                    {canOpenStaffingChange && <Button size="small" disabled={busy} onClick={() => onOpenStaffingChange(row.responseId!)}>발령 상세 보기</Button>}
                  </Stack>
                </TableCell>
              </TableRow>;
            })}</TableBody>
          </Table>
        </TableContainer> : <Box role="list" aria-label="대량 작업 행별 결과">
          {rows.slice(0, visibleCount).map((row) => <Box role="listitem" key={row.rowNumber}>
            <ListRow label={`${row.rowNumber}행`} trailing={<BulkStatus value={row.status} />} description={row.errorMessage} inset="none" />
            <Divider />
          </Box>)}
        </Box>}
      </ResourceState>
      {rows.length > visibleCount && <Button variant="outlined" onClick={() => setVisibleCount((value) => value + 25)}>더 보기 ({Math.min(visibleCount, rows.length).toLocaleString()} / {rows.length.toLocaleString()})</Button>}
    </>}
  </Stack>;
}
